// Data plumbing for the Matches tab: session-cache keys, ref-counted live Firestore
// listeners (favorites doc + listing-page-views doc), preload warm-up, and the
// minisite mutations.
//
// Firestore write-shape intentionally REUSES utils/Haim/firestoreUtils (the exact
// functions the legacy match page uses) so the favorites doc stays byte-compatible with
// every other surface: adds write the listing and ONLY its own presentation fields,
// removes delete ONLY the removed listing's own additionalDetails entries — per-listing edits (realtor notes,
// description overrides, hidden pictures) and any field this tab doesn't manage are
// never clobbered.
import { useEffect, useState } from "react";
import { db, firebase } from "firebaseConfig";
import { GenericConverter } from "utils/firestoreConverters";
import {
  getFavoritesListingsQuery,
  createFavoriteIfNotExists,
  addListingsToFavorites,
  removeListingsFromFavorites,
  getRealtorListingsBlacklistQuery
} from "utils/Haim/firestoreUtils";
import { pickMinisitePage } from "utils/minisite/minisitePage";
import {
  getListingPicturesVisibility,
  getStringHiddenListingPicturesFieldsIndex
} from "components/_dashboard/listingsMatch/ListingPicturesVisibilty";
import type { ListingPicturesVisibility } from "components/_dashboard/listingsMatch/ListingPicturesVisibilty";
import { getStringListingDescriptionOverrideIndex } from "components/_dashboard/listingsMatch/ListingMatchDescriptionOverride";
import { fetchListingsByIds } from "utils/listings/fetchListings";
import {
  LISTING_PAGE_VIEWS_COLLECTION_NAME
} from "../../../../../../@types/sharedSchema";
import type {
  RealEstateContact,
  ListingsRealtorBlacklist,
  ListingPageViewsDocument,
  FavoriteListingsOfContact
} from "../../../../../../@types/sharedSchema";
import {
  getCached,
  setCached,
  fetchCached,
  subscribeCached
} from "../../data/sessionCache";
import type { MatchHit } from "./matchesSearch";

// ── cache keys ──────────────────────────────────────────────────────────────────

export const favoritesCacheKey = (contactId: string): string => `matches:${contactId}:favorites`;
export const viewsCacheKey = (contactId: string): string => `matches:${contactId}:views`;
export const favoriteListingsCacheKey = (contactId: string): string =>
  `matches:${contactId}:favorite-listings`;
export const searchCacheKey = (contactId: string): string => `matches:${contactId}:search`;
export const profileSuggestionDismissedKey = (contactId: string): string =>
  `matches:${contactId}:profile-suggestion-dismissed`;
const blacklistCacheKey = (userEmail: string): string => `matches:blacklist:${userEmail}`;

// ── one-shot fetchers ───────────────────────────────────────────────────────────

export async function fetchFavoritesDocOnce(
  userEmail: string,
  contactId: string
): Promise<FavoriteListingsOfContact | null> {
  const snapshot = await getFavoritesListingsQuery(userEmail, contactId).get();
  return pickMinisitePage(snapshot.docs)?.data() ?? null;
}

async function fetchViewsDocOnce(favoritesDocId: string): Promise<ListingPageViewsDocument | null> {
  const snapshot = await db
    .collection(LISTING_PAGE_VIEWS_COLLECTION_NAME)
    .doc(favoritesDocId)
    .withConverter(GenericConverter<ListingPageViewsDocument>())
    .get();
  return snapshot.exists ? (snapshot.data() as ListingPageViewsDocument) : null;
}

export function fetchBlacklistCached(userEmail: string): Promise<ListingsRealtorBlacklist | null> {
  return fetchCached(
    blacklistCacheKey(userEmail),
    async () => (await getRealtorListingsBlacklistQuery(userEmail).get()).data() ?? null,
    { maxAgeMs: 5 * 60_000 }
  );
}

// ── ref-counted live listeners ──────────────────────────────────────────────────
// One favorites-query listener + one listing-page-views doc listener per open contact
// page, shared between the tab body and the badge hook. Snapshots land in the session
// cache, so every consumer renders from the same source and reopening is instant.

type MatchesLiveEntry = {
  refCount: number;
  unsubscribeFavorites: () => void;
  unsubscribeViews?: () => void;
  viewsDocId?: string;
};

const matchesLiveEntries = new Map<string, MatchesLiveEntry>();

function syncViewsListener(
  entry: MatchesLiveEntry,
  contactId: string,
  favoritesDocId: string | undefined
): void {
  if (entry.viewsDocId === favoritesDocId) return;
  entry.unsubscribeViews?.();
  entry.unsubscribeViews = undefined;
  entry.viewsDocId = favoritesDocId;
  if (!favoritesDocId) {
    setCached<ListingPageViewsDocument | null>(viewsCacheKey(contactId), null);
    return;
  }
  entry.unsubscribeViews = db
    .collection(LISTING_PAGE_VIEWS_COLLECTION_NAME)
    .doc(favoritesDocId)
    .withConverter(GenericConverter<ListingPageViewsDocument>())
    .onSnapshot(
      (snapshot) => {
        setCached<ListingPageViewsDocument | null>(
          viewsCacheKey(contactId),
          snapshot.exists ? (snapshot.data() as ListingPageViewsDocument) : null
        );
      },
      () => undefined // listener errors leave the cache stale, never crash the page
    );
}

/**
 * Start (or join) the live listeners for a contact. Returns a release function —
 * idempotent-safe to call from multiple hooks; the underlying listeners stop when the
 * last consumer releases (page unmount).
 */
export function ensureMatchesLive(userEmail: string, contactId: string): () => void {
  const existing = matchesLiveEntries.get(contactId);
  if (existing) {
    existing.refCount += 1;
    return () => releaseMatchesLive(contactId);
  }
  const entry: MatchesLiveEntry = { refCount: 1, unsubscribeFavorites: () => undefined };
  matchesLiveEntries.set(contactId, entry);
  entry.unsubscribeFavorites = getFavoritesListingsQuery(userEmail, contactId).onSnapshot(
    (snapshot) => {
      // The page the buyer was sent, when there are several (`pickMinisitePage`).
      const favorites = pickMinisitePage(snapshot.docs)?.data() ?? null;
      setCached<FavoriteListingsOfContact | null>(favoritesCacheKey(contactId), favorites);
      syncViewsListener(entry, contactId, favorites?.id);
    },
    () => undefined
  );
  return () => releaseMatchesLive(contactId);
}

function releaseMatchesLive(contactId: string): void {
  const entry = matchesLiveEntries.get(contactId);
  if (!entry) return;
  entry.refCount -= 1;
  if (entry.refCount > 0) return;
  matchesLiveEntries.delete(contactId);
  entry.unsubscribeFavorites();
  entry.unsubscribeViews?.();
}

/** Cache-backed React binding without a fetcher (values are pushed by the listeners). */
export function useCachedLiveValue<T>(key: string): T | undefined {
  const [, forceRender] = useState(0);
  useEffect(() => subscribeCached(key, () => forceRender((n) => n + 1)), [key]);
  return getCached<T>(key);
}

// ── favorites → listing data reconciliation ─────────────────────────────────────

/**
 * Keeps the cached listing-data array aligned with the favorites doc's listingIds.
 * Display order is newest-added first (reverse of the array — the legacy convention).
 * Missing docs are fetched by id (Firestore point reads with retry); listings whose doc
 * no longer exists are silently dropped, same as the legacy minisite.
 */
export async function reconcileFavoriteListingsData(
  contactId: string,
  listingIds: string[]
): Promise<void> {
  const key = favoriteListingsCacheKey(contactId);
  const orderedIds = [...listingIds].reverse();
  const cached = getCached<MatchHit[]>(key) ?? [];
  const byId = new Map(cached.map((listing) => [listing.objectID, listing]));
  const missingIds = orderedIds.filter((id) => !byId.has(id));
  if (missingIds.length > 0) {
    const fetched = await fetchListingsByIds(missingIds);
    fetched.forEach((listing) => byId.set(listing.objectID, listing as MatchHit));
  }
  const next = orderedIds
    .map((id) => byId.get(id))
    .filter((listing): listing is MatchHit => Boolean(listing));
  const prevIdsKey = cached.map((l) => l.objectID).join(",");
  const nextIdsKey = next.map((l) => l.objectID).join(",");
  // Also write when the key was never populated ('' === '' id-keys otherwise skip the
  // very first write when NO favorite id resolves to a live listing doc — leaving the
  // minisite grid on skeletons forever instead of its empty state).
  if (prevIdsKey !== nextIdsKey || getCached<MatchHit[]>(key) === undefined) {
    setCached(key, next);
  }
}

// ── preload (contacts-table row hover + page mount) ─────────────────────────────
// One-shot reads only — persistent listeners here would leak one per hovered row.
// Everything dedups through fetchCached, so hover spam is idempotent-cheap.

export function preloadMatchesData(contact: RealEstateContact | null | undefined): void {
  const userEmail = firebase.auth().currentUser?.email;
  if (!userEmail || !contact?.id) return;
  const contactId = contact.id;
  void fetchCached<FavoriteListingsOfContact | null>(
    favoritesCacheKey(contactId),
    () => fetchFavoritesDocOnce(userEmail, contactId),
    { maxAgeMs: 120_000 }
  )
    .then((favorites) => {
      if (!favorites?.id) return;
      void fetchCached<ListingPageViewsDocument | null>(
        viewsCacheKey(contactId),
        () => fetchViewsDocOnce(favorites.id as string),
        { maxAgeMs: 120_000 }
      ).catch(() => undefined);
      void reconcileFavoriteListingsData(contactId, favorites.listingIds ?? []).catch(
        () => undefined
      );
    })
    .catch(() => undefined);
  // NOTE: the listing search itself needs no per-contact warm-up — it is a plain
  // server call authenticated with the user's Firebase token, with no client-side
  // handle to mint or cache first.
}

// ── minisite mutations ──────────────────────────────────────────────────────────

// Serializes lazy creation per contact so a double-click can't create two docs.
const favoritesCreationInFlight = new Map<string, Promise<void>>();

async function ensureFavoritesDoc(
  userEmail: string,
  contactId: string
): Promise<FavoriteListingsOfContact> {
  const cached = getCached<FavoriteListingsOfContact | null>(favoritesCacheKey(contactId));
  if (cached?.path) return cached;
  let creating = favoritesCreationInFlight.get(contactId);
  if (!creating) {
    creating = createFavoriteIfNotExists(userEmail, contactId).then(() => undefined);
    favoritesCreationInFlight.set(contactId, creating);
    void creating.finally(() => favoritesCreationInFlight.delete(contactId));
  }
  await creating;
  const fresh = await fetchFavoritesDocOnce(userEmail, contactId);
  if (!fresh?.path) throw new Error(`Favorites doc missing after create for ${contactId}`);
  setCached<FavoriteListingsOfContact | null>(favoritesCacheKey(contactId), fresh);
  return fresh;
}

/**
 * Add a listing to the contact's minisite, creating the favorites doc lazily.
 *
 * The listing arrives as the realtor presents it on every page — their text and the
 * photos they hide (never the per-buyer note) — from their saved record, the authority
 * since 2026-09-30 (`addListingsToFavorites` → `getListingPresentationForAdd`). It used
 * to copy the realtor's last per-contact edit from another of their pages, which cannot
 * see a listing edited before it was on any page.
 */
export async function addListingToContactMinisite(
  userEmail: string,
  contactId: string,
  listing: MatchHit
): Promise<void> {
  const favorites = await ensureFavoritesDoc(userEmail, contactId);
  if (favorites.listingIds?.includes(listing.objectID)) return;
  await addListingsToFavorites(favorites.path as string, [listing.objectID]);
  // Optimistic cache patches — the live listener (when active) converges to the same
  // state (including the prior-edit fields); these keep the badge and a cold minisite
  // sub-tab instant.
  setCached<FavoriteListingsOfContact | null>(favoritesCacheKey(contactId), {
    ...favorites,
    listingIds: [...(favorites.listingIds ?? []), listing.objectID]
  });
  const cachedListings = getCached<MatchHit[]>(favoriteListingsCacheKey(contactId)) ?? [];
  if (!cachedListings.some((l) => l.objectID === listing.objectID)) {
    setCached(favoriteListingsCacheKey(contactId), [listing, ...cachedListings]);
  }
}

/**
 * Remove a listing from the minisite. Reuses the legacy remove (arrayRemove + targeted
 * FieldValue.delete of that listing's own edits) — other listings' edits and unmanaged
 * doc fields are untouched.
 */
export async function removeListingFromContactMinisite(
  contactId: string,
  listingId: string
): Promise<void> {
  const favorites = getCached<FavoriteListingsOfContact | null>(favoritesCacheKey(contactId));
  if (!favorites?.path) return;
  await removeListingsFromFavorites(favorites.path as string, [listingId]);
  setCached<FavoriteListingsOfContact | null>(favoritesCacheKey(contactId), {
    ...favorites,
    listingIds: (favorites.listingIds ?? []).filter((id) => id !== listingId)
  });
  const cachedListings = getCached<MatchHit[]>(favoriteListingsCacheKey(contactId)) ?? [];
  setCached(
    favoriteListingsCacheKey(contactId),
    cachedListings.filter((l) => l.objectID !== listingId)
  );
}

// ── per-listing edit suite (favorites-doc field writes, byte-compatible with the
//    legacy dialogs + the public minisite reader) ────────────────────────────────

// Parity with ListingCardNotes.getStringListingListingNotes (not imported — that
// module is a legacy feature component; the path is schema-documented).
const listingNotesFieldKey = (listingId: string): string =>
  `additionalDetails.${listingId}.listingNotes`;

/**
 * What the edit dialog manages for one listing inside one minisite.
 *
 * ⚠️ TWO SCOPES. The presentation fields — `descriptionOverride`, `picturesVisibilityMode`,
 * `hiddenPictures` — describe the PROPERTY and are saved once for the realtor
 * (`saveListingPresentation`), then fanned out to every minisite holding the listing. They are read
 * here, so the dialog can show the current values, but they are NOT written by
 * `saveListingMinisiteEdits`: writing them per contact is what made a realtor edit the same listing
 * forty times.
 *
 * The rest describe this BUYER and stay on their minisite alone.
 */
export type MinisiteListingEdits = {
  /** READ-ONLY here — saved globally. See above. */
  descriptionOverride: string; // "" = no override (original description shows)
  /** READ-ONLY here — saved globally. */
  picturesVisibilityMode: ListingPicturesVisibility; // all | some | none
  /** READ-ONLY here — saved globally. */
  hiddenPictures: string[]; // imageUrls entries to hide — meaningful when mode === "some"
  listingNotes: string;
  visited: boolean; // ביקר בנכס
  liked: boolean; // אהב (mutually exclusive with disliked)
  disliked: boolean; // לא אהב
};

/** Current edit state of a listing, read from the favorites doc. */
export function readListingMinisiteEdits(
  favorites: FavoriteListingsOfContact,
  listingId: string
): MinisiteListingEdits {
  const details = favorites.additionalDetails?.[listingId];
  return {
    descriptionOverride: details?.descriptionOverride ?? "",
    picturesVisibilityMode: getListingPicturesVisibility(listingId, favorites),
    hiddenPictures: details?.hiddenPictures?.pictures ?? [],
    listingNotes: details?.listingNotes ?? "",
    visited: Boolean(favorites.listingIdsUserVisited?.includes(listingId)),
    liked: Boolean(favorites.listingIdsUserLiked?.includes(listingId)),
    disliked: Boolean(favorites.listingIdsUserDisliked?.includes(listingId))
  };
}

const withToggled = (arr: string[] | undefined, id: string, on: boolean): string[] => {
  const base = (arr ?? []).filter((x) => x !== id);
  return on ? [...base, id] : base;
};

/**
 * Persist the full edit set for one listing in one Firestore update on the contact's
 * favorites doc — the exact field paths the legacy dialogs write and the public
 * minisite reads (description/hiddenPictures under additionalDetails.{listingId},
 * buyer state via the listingIdsUser* arrays). Stamps updatedTime so this minisite
 * ranks as the realtor's most-recent edit source for add-time reuse.
 */
export async function saveListingMinisiteEdits(
  contactId: string,
  listingId: string,
  edits: Pick<MinisiteListingEdits, "listingNotes" | "visited" | "liked" | "disliked">
): Promise<void> {
  const favorites = getCached<FavoriteListingsOfContact | null>(favoritesCacheKey(contactId));
  if (!favorites?.path) throw new Error(`No favorites doc for ${contactId}`);

  const arrayUnion = firebase.firestore.FieldValue.arrayUnion(listingId);
  const arrayRemove = firebase.firestore.FieldValue.arrayRemove(listingId);

  /*
   * ⚠️ PER-CONTACT FIELDS ONLY. The description override and the hidden-picture set are NOT written
   * here any more: they describe the property, are saved once per realtor through
   * `saveListingPresentation`, and are fanned out to every minisite holding the listing. Writing
   * them here too would race that fan-out and resurrect the old "edit it forty times" behaviour.
   */
  const update: Record<string, unknown> = {
    updatedTime: firebase.firestore.FieldValue.serverTimestamp(),
    [listingNotesFieldKey(listingId)]: edits.listingNotes.trim(),
    listingIdsUserVisited: edits.visited ? arrayUnion : arrayRemove,
    listingIdsUserLiked: edits.liked && !edits.disliked ? arrayUnion : arrayRemove,
    listingIdsUserDisliked: edits.disliked && !edits.liked ? arrayUnion : arrayRemove
  };

  await db.doc(favorites.path as string).update(update);

  // Optimistic favorites patch (the live listener converges to the same state).
  const liked = edits.liked && !edits.disliked;
  const disliked = edits.disliked && !edits.liked;
  setCached<FavoriteListingsOfContact | null>(favoritesCacheKey(contactId), {
    ...favorites,
    listingIdsUserVisited: withToggled(favorites.listingIdsUserVisited, listingId, edits.visited),
    listingIdsUserLiked: withToggled(favorites.listingIdsUserLiked, listingId, liked),
    listingIdsUserDisliked: withToggled(favorites.listingIdsUserDisliked, listingId, disliked),
    additionalDetails: {
      ...(favorites.additionalDetails ?? {}),
      [listingId]: {
        // The presentation half is left exactly as the listener last saw it — the server's fan-out
        // owns it, so guessing here would briefly show the realtor a value nobody wrote.
        ...(favorites.additionalDetails?.[listingId] ?? {
          descriptionOverride: "",
          hiddenPictures: { enabled: false, pictures: [] }
        }),
        listingNotes: edits.listingNotes.trim()
      }
    }
  });
}

// NOTE: the per-minisite watermark-free toggle writer (setMinisiteShowWatermarkFreeImages)
// was removed 2026-07 — clean versions now always display where one exists, so
// FavoriteListingsOfContact.showWatermarkFreeImages is deprecated and no longer written.
